/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Vu Dinh Luan (vdluan@rsupport.com)
 *
 * @description
 *
 * @created at Fri Sep 13 2024
 **/

import { useCallback, useRef } from "react";
import { useTheme } from "@emotion/react";

import { Maybe } from "@/frameworks/libraries/components";
import { Column, Icon, Menu, Row, Typography } from "@/frameworks/designStuff/components";
import { _T } from "@rfice/language";
import { POPOVER_ID } from "@/frameworks/core/commons";
import { useCreateWorkGroup, usePopover } from "@/frameworks/core/hooks";

import ChangeColorPalette from "./ChangeColorPalette";
import * as S from "../emotion";
import { marginBottomStyle } from "../emotion";

export default function ImageField() {
	const theme = useTheme();
	const ref = useRef<HTMLInputElement>(null);
	const {
		name,
		src,
		checkedID,
		selectedColorName,
		handlers: { handleRepresentativeImg, handleSetImageUrl, handleCheckedType }
	} = useCreateWorkGroup();

	const popover = usePopover();

	const handleChange = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			popover.hideAll();
			try {
				handleCheckedType(event.target.id as Group.CheckedType["ID"]);

				if (event.target.id === "selectImage") {
					const target = event.target as HTMLInputElement;

					if (!target.files?.length) throw new Error("선택된 파일이 없습니다.");

					handleRepresentativeImg(target.files[0]);
					const base64 = await processFile(target.files[0]);
					handleSetImageUrl(base64);
				}
			} catch (error) {
				console.error(error);
			}
		},
		[checkedID]
	);

	const handleImgRemove = () => {
		popover.hideAll();
		if (!ref.current) return;
		ref.current.value = "";
		handleSetImageUrl(undefined);
		handleCheckedType("selectColor");
	};

	const processFile = (file: Blob) => {
		return new Promise<string>((resolve) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onloadend = () => {
				resolve(reader.result as string);
			};
		});
	};
	const ImagePopover = () => {
		return (
			<Menu.List
				style={{
					width: "160px",
					height: "81px",
					padding: "8px",
					background: theme.colors.neutral_00_100,
					borderRadius: "8px",
					boxShadow: "0px 8px 16px 0px rgba(0, 0, 0, 0.20), 0px 0px 4px 0px rgba(0, 0, 0, 0.04)"
				}}
			>
				<Menu.Item>
					<Typography.label typoSize="Body3_MD" htmlFor="selectImage">
						{_T("이미지 불러오기")}
					</Typography.label>
					<S.FakeInput
						ref={ref}
						id="selectImage"
						type="file"
						accept="image/png, image/gif, image/jpeg"
						onChange={handleChange}
					/>
				</Menu.Item>
				<Menu.Item css={{ ":hover": { button: { background: "#fff" } } }}>
					<Typography
						typoSize="Body3_MD"
						typoColor={src ? "warning_80_100" : "warning_10_100"}
						onClick={handleImgRemove}
					>
						{_T("사진 제거")}
					</Typography>
				</Menu.Item>
			</Menu.List>
		);
	};
	return (
		<S.FieldBox>
			<S.Name typoSize="Body4" css={marginBottomStyle(4)}>
				{_T("대표 이미지")}
			</S.Name>
			<S.UploadWrap verticalAlign="center">
				<S.ImageBox
					bgcolor={theme.colors[selectedColorName]}
					onClick={(e) => {
						popover.show({
							popoverID: POPOVER_ID.COLOR_PALETTE,
							content: <ImagePopover />,
							anchorEl: e.currentTarget,
							position: { x: 10, y: 0 },
							direction: { x: "right", y: "center" }
						});
					}}
				>
					<S.IconWrapper>
						<Icon name="Edit24" />
					</S.IconWrapper>
					<Maybe
						test={checkedID === "selectImage"}
						fallback={<S.NameTypo typoSize="Head6">{name}</S.NameTypo>}
					>
						<Maybe
							test={Boolean(src)}
							fallback={
								<S.EmptyUploadWrapper horizonAlign="center" verticalAlign="center">
									<S.EmptyImgIcon name="Image24" size={24} />
									<S.UploadText typoSize="Body5">{_T("이미지 업로드")}</S.UploadText>
								</S.EmptyUploadWrapper>
							}
						>
							<S.Img src={src} alt="img" />
						</Maybe>
					</Maybe>
				</S.ImageBox>
				<Column css={{ position: "relative" }}>
					<Row>
						<ChangeColorPalette disabled={Boolean(src)} />
					</Row>
				</Column>
			</S.UploadWrap>
		</S.FieldBox>
	);
}
